<template>
  <div class="d-flex flex-column h-100 p-lg-1 p-0">
    <div class="searchArea">
      <typeahead-search
        ref="searchField"
        enable-keyboard-selection
        :value="searchModel.getQuery()"
        :placeholder="$t('messages.search_placeholder', { folder: currentFolderName })"
        :folder-name="currentFolderName"
        @selected="runTypeaheadSearch"
        @resetSearch="clearAllInput"
        @inputChanged="getAllSubscriptionsWhenBlank"
        @updateValueInput="setTypeaheadSearchValue"
        @advanceSearchClick="showAdvanceSearchModal"
      />
    </div>
    <ObservingContainer class="threads scrollbar mt-1 mt-lg-0 pb-lg-2" :options="observerOptions">
      <ThreadCompactHeader v-if="!isMobile && selectedThreadView === threadViewEnum.COMPACT" class="color-grey-dark" />
      <CustomDraggable
        :key="subscriptionFolder.id"
        :group="'messages'"
        :ghost-class="'ghost-subscription'"
        :drag-class="'dragging-subscription'"
        :filter="'draft'"
        :disabled="!draggable"
        draggable="subscription-container"
        multi-drag
        use-child-ref
        @start="handleDragStart"
        @move="handleDragMove"
        @end="handleDragEnd"
      >
        <transition-group name="list" class="sortable-container" :data-is-multiple="isMultipleItemsSelected">
          <subscription-container
            v-for="subscription in sortedThreads"
            :key="`s-${subscription.id}`"
            :subscription-data="subscription"
            :draggable="draggable"
            :can-show-folder-badge="canShowFolderBadge"
            :is-mobile="isMobile"
            :data-folder-id="subscription.folderId"
            :data-folder-type="subscription.folderType"
            :data-subscription-id="subscription.subscriptionId"
            @draggingChange="emitDraggingChange"
          />
        </transition-group>
      </CustomDraggable>
      <b-spinner v-if="isLoadingThreads" class="loading-spinner" />
      <div v-if="!moreSubscriptionsExist && !isLoadingThreads" class="p-3 text-center">
        {{ $getText('MESSAGE_NO_MORE_THREADS') }}
      </div>
      <ObservedTarget v-if="isObservable" @onIntersecting="onBottomReached" />
    </ObservingContainer>
    <aula-modal
      ref="advanceSearchModal"
      header-icon="icon-Aula_search"
      header-text="MESSAGE_ADVANCED_SEARCH"
      ok-text="ARIA_LABEL_SEARCH"
      :css-class="advanceSearchClass"
      @cancelClicked="hideAdvancedSearchModal"
      @okClicked="runSearch(advancedSearchQuery)"
    >
      <MessageAdvancedSearch
        class="mx-lg-3"
        :query="advancedSearchQuery"
        :subject="advancedSearchSubject"
        :message-content="advancedSearchMessageContent"
        :filter="advancedSearchFilter"
        :from-date="advancedSearchFromDate"
        :to-date="advancedSearchToDate"
        :thread-creators="advancedSearchThreadCreators"
        :participants="advancedSearchParticipants"
        :with-attachments="advancedSearchWithAttachments"
        :folders="chosenFolderAndMailOwner"
        @updateSearchQuery="setAdvancedSearchQuery"
        @clearAllInput="clearAllInput"
        @updateSearchSubject="setAdvancedSearchSubject"
        @updateSearchMessageContent="setAdvancedSearchMessageContent"
        @updateSearchSelectedFilter="setAdvancedSearchFilter"
        @updateSearchFromDate="setAdvancedSearchFromDate"
        @updateSearchToDate="setAdvancedSearchToDate"
        @updateSearchThreadCreators="setAdvancedSearchThreadCreators"
        @updateSearchParticipants="setAdvancedSearchParticipants"
        @updateSearchWithAttachments="setAdvancedSearchWithAttachments"
      />
    </aula-modal>
    <AulaModal
      ref="primarySubscriptionWarning"
      ok-text="YES"
      @cancelClicked="$refs.primarySubscriptionWarning.hide()"
      @okClicked="
        moveDraggedSubscription();
        $refs.primarySubscriptionWarning.hide();
      "
    >
      <p v-html="$t('messages.primary_thread_moving_warning')" />
    </AulaModal>
  </div>
</template>
<script>
import { types } from '../../store/types/types';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import TypeaheadSearch from '../shared/TypeaheadSearch';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { permissionEnum } from '../../../shared/enums/permissionEnum.ts';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import { messageOwnerTypes } from '../../../shared/enums/messageOwnerTypes';
import { messageFiltersEnum } from '../../../shared/enums/messageFiltersEnum';
import $ from 'jquery';
import requestCancelTokenMixin from '../../../shared/mixins/requestCancelTokenMixin';
import SubscriptionContainer from './SubscriptionContainer';
import { messageProviderKeyEnum } from '../../../shared/enums/messageProviderKeyEnum';
import * as dateUtil from '../../../shared/utils/dateUtil';
import MessageAdvancedSearch from '../../../shared/PageFragments/MessageAdvancedSearch/MessageAdvancedSearch';
import ObservingContainer from '../../../shared/libs/intersection-observer/components/Container';
import ObservedTarget from '../../../shared/libs/intersection-observer/components/Target';
import ThreadCompactHeader from '../../../shared/PageFragments/ThreadViews/ThreadCompactHeader';
import { threadViewEnum } from '../../../shared/enums/threadViewEnum';
import CustomDraggable from '../shared/CustomDraggable.vue';
import isNumber from 'lodash/isNumber';
import { InboxFolderType } from '../../../shared/models/inboxFolder.model';
import isEqual from 'lodash/isEqual';
import { deselectAllManagers } from '../../../shared/composables/sortableRegistry';
import { useUserProfileStore } from '../../../shared/stores';

const FOLDER_NORMAL = 'normal';

export default {
  components: {
    ThreadCompactHeader,
    ObservedTarget,
    ObservingContainer,
    SubscriptionContainer,
    TypeaheadSearch,
    MessageAdvancedSearch,
    CustomDraggable,
  },
  mixins: [requestCancelTokenMixin],
  provide() {
    return {
      [messageProviderKeyEnum.HANDLE_DRAG_START]: this.handleDragStart,
      [messageProviderKeyEnum.HANDLE_DRAG_MOVE]: this.handleDragMove,
      [messageProviderKeyEnum.HANDLE_DRAG_END]: this.handleDragEnd,
    };
  },
  inject: {
    setConversationLoadingState: messageProviderKeyEnum.SET_CONVERSATION_LOADING_STATE,
    restartPollingService: messageProviderKeyEnum.RESTART_POLLING_SERVICE,
    getIsDraftSelected: messageProviderKeyEnum.IS_DRAFT_SELECTED,
    getSearchModel: messageProviderKeyEnum.SEARCH_MODEL,
    getSelectedThreadView: messageProviderKeyEnum.SELECTED_THREAD_VIEW,
    deleteSelectedMessages: messageProviderKeyEnum.DELETE_SELECTED_MESSAGES,
    moveSubscriptionsToFolder: messageProviderKeyEnum.MOVE_SUBSCRIPTIONS_TO_FOLDER,
    fetchThreads: messageProviderKeyEnum.FETCH_THREADS,
  },
  props: {
    draggable: { type: Boolean, default: false },
  },
  emits: ['draggingChange', 'select'],
  data: function () {
    return {
      title: 'Beskeder',
      openThreadCancelTokenSource: null,
      advancedSearchQuery: '',
      advancedSearchSubject: '',
      advancedSearchMessageContent: '',
      advancedSearchFilter: messageFiltersEnum.FILTER_ALL,
      advancedSearchFromDate: undefined,
      advancedSearchToDate: undefined,
      advancedSearchThreadCreators: [],
      advancedSearchParticipants: [],
      advancedSearchWithAttachments: false,
      threadViewEnum,
      targetFolder: null,
      draggedSubscriptions: [],
      canShowFolderBadge: false,
    };
  },
  computed: {
    ...mapGetters({
      folders: types.MESSAGES_GET_FOLDERS,
      messages: types.MESSAGES_GET,
      isLoadingThreads: types.IS_LOADING_THREADS,
      subscriptions: types.MESSAGES_GET_SUBSCRIPTIONS,
      subscriptionsIndex: types.MESSAGES_GET_SUBSCRIPTIONS_INDEX,
      draftThreads: types.MESSAGES_GET_DRAFT_THREADS,
      activeChildren: types.GET_ACTIVE_CHILDREN,
      activeInstitutions: types.GET_ACTIVE_INSTITUTIONS,
      selectedThreadIds: types.MESSAGES_GET_CHOSEN_THREAD_IDS,
      selectedBundleIds: types.MESSAGES_GET_CHOSEN_BUNDLE_IDS,
      isMobile: types.GET_IS_MOBILE,
      subscriptionPressed: types.MESSAGES_GET_SUBSCRIPTIONS_ONPRESS_MOBILEVIEW,
      notifications: types.GET_NOTIFICATIONS,
      moreSubscriptionsExist: types.MESSAGES_GET_MORE_SUBSCRIPTIONS_EXIST,
      isSteppedUp: types.GET_GLOBAL_STEPPED_UP,
      chosenFolderAndMailOwner: types.MESSAGES_GET_CHOSEN_FOLDER_AND_MAIL_OWNER,
      hasPermission: types.HAS_PERMISSION,
      inSearch: types.MESSAGES_GET_IN_SEARCH,
      messageSearchQuery: types.MESSAGES_GET_ACTIVE_SEARCH_QUERY,
      commonInboxes: types.MESSAGES_GET_COMMON_INBOXES,
      selectedDraft: types.MESSAGES_GET_SELECTED_DRAFT,
      subscriptionsAndBundleThreads: types.MESSAGES_GET_SUBSCRIPTIONS_AND_BUNDLE_THREADS,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    isMultipleItemsSelected() {
      return this.selectedThreadIds.length + this.selectedBundleIds.length > 1;
    },
    subscriptionFolder() {
      const subscriptionFolderId = isNumber(this.chosenFolderAndMailOwner.folderId)
        ? this.chosenFolderAndMailOwner.folderId
        : null;
      const subscriptionFolderType = isEqual(this.chosenFolderAndMailOwner.folderType, FOLDER_NORMAL)
        ? InboxFolderType.FOLDER
        : InboxFolderType.DELETED_FOLDER;

      if (!isNumber(subscriptionFolderId)) {
        return {
          type: InboxFolderType.INBOX,
          id: this.chosenFolderAndMailOwner.mailOwnerId || null,
        };
      }

      return {
        type: subscriptionFolderType,
        id: subscriptionFolderId,
      };
    },
    advanceSearchClass() {
      let advanceSearchClass = 'aula-modal-lg';
      if (this.isMobile) {
        advanceSearchClass += ' color-grey-light';
      }
      return advanceSearchClass;
    },
    observerOptions() {
      return { rootMargin: '50%' };
    },
    isObservable() {
      return this.sortedThreads.length > 0;
    },
    selectedThreadView() {
      return this.getSelectedThreadView();
    },
    searchModel() {
      return this.getSearchModel();
    },
    isDraftSelected() {
      return this.getIsDraftSelected();
    },
    sortedThreads() {
      const shownSubscriptions = this.subscriptions.filter(subscription => !subscription.deleted);
      let draftThreads = [];
      if (
        (this.chosenFolderAndMailOwner.filter === messageFiltersEnum.FILTER_ALL ||
          this.chosenFolderAndMailOwner.filter === messageFiltersEnum.FILTER_DRAFT) &&
        this.searchModel.query.length === 0 &&
        !this.searchModel.getIsUsingAdvancedSearch()
      ) {
        draftThreads = Object.values(this.draftThreads);
      }
      return [...draftThreads.reverse(), ...shownSubscriptions];
    },
    currentSubscriptionId() {
      return this.$route.params?.id;
    },
    currentFolderName() {
      return this.chosenFolderAndMailOwner.folderName ?? this.$t('messages.inboxes');
    },
  },
  watch: {
    inSearch() {
      this.restartPollingService();
    },
    currentSubscriptionId() {
      this.checkIfOpenThread();
    },
  },
  mounted() {
    this.checkIfOpenThread();
    window.addEventListener('keyup', this.scrollIntoViewOnFocused);
  },
  destroyed() {
    window.removeEventListener('keyup', this.scrollIntoViewOnFocused);
  },
  methods: {
    ...mapActions({
      updateThreadViewAction: types.UPDATE_THREADS_VIEW,
      openThread: types.SELECT_SUBSCRIPTION,
      searchSubscriptions: types.SEARCH_SUBSCRIPTIONS,
      deleteNotifications: types.DELETE_NOTIFICATIONS,
      loadMoreSubscriptions: types.LOAD_MORE_SUBSCRIPTIONS,
      moveMessagesToFolderAction: types.MOVE_THREADS_TO_FOLDER,
      loadThreadsInBundleById: types.LOAD_THREADS_IN_BUNDLE_BY_ID,
    }),
    ...mapMutations({
      setStepUpNotification: types.MUTATE_SET_NOTIFICATION_STEP_UP,
      setIsLoadingThreads: types.MUTATE_IS_LOADING_THREADS,
      setMessageSearchQuery: types.MUTATE_SUBSCRIPTIONS_SEARCH_QUERY,
      setChosenFilter: types.MUTATE_CHOSEN_FILTER,
      setSelectedThreadIds: types.MUTATE_CHOSEN_THREAD_IDS,
      setSelectedThreadFolderIds: types.MUTATE_CHOSEN_THREAD_FOLDER_IDS,
      setSelectedSubscriptionsIds: types.MUTATE_CHOSEN_SUBSCRIPTION_IDS,
      setSelectedBundleIds: types.MUTATE_CHOSEN_BUNDLE_IDS,
      deleteSubscriptionById: types.MUTATE_DELETE_SUBSCRIPTION_BY_ID,
      uncheckAllSubscriptions: types.MUTATE_UNCHECK_ALL_SUBSCRIPTIONS,
    }),
    emitDraggingChange(isDragging) {
      this.$emit('draggingChange', isDragging);
    },
    emitSelect(folderId) {
      this.$emit('select', folderId);
    },
    handleDragStart(event) {
      let items = [event.item];
      if (event.items.length > 0) {
        items = event.items;
      }
      this.draggedSubscriptions = items.map(item => ({
        subscriptionId: parseInt(item.dataset.subscriptionId) || null,
        folderId: parseInt(item.dataset.folderId) || null,
        isPrimary: Boolean(item.dataset.isPrimary),
        isLast: Boolean(item.dataset.isLast),
        bundleId: parseInt(item.dataset.bundleId) || null,
      }));
      this.emitDraggingChange(true);
    },
    handleDragMove(event) {
      this.targetFolder = {
        id: parseInt(event.related.dataset.id) || null,
        type: event.related.dataset.type,
      };
    },
    handleDragEnd(event) {
      this.emitDraggingChange(false);

      if (this.getIsDroppable(event.originalEvent.target) === false) {
        return;
      }

      if (this.targetFolder?.type !== InboxFolderType.DELETED_FOLDER) {
        this.handleSubscriptionMoved();
        return;
      }

      if (this.draggedSubscriptions.length > 1) {
        this.deleteSelectedMessages();
        return;
      }

      const subscriptionComponent = event.item?.__vue__ ?? event.item?.firstChild?.__vue__;
      subscriptionComponent?.deleteBtnClicked();
    },
    handleSubscriptionMoved() {
      const hasPrimarySubscription = this.draggedSubscriptions.some(subscription => subscription.isPrimary);

      const allPrimaryHaveItsBundle = this.draggedSubscriptions
        .filter(subscription => subscription.isPrimary)
        .every(primarySub =>
          this.draggedSubscriptions.some(otherSub => otherSub.subscriptionId === primarySub.bundleId)
        );

      if (hasPrimarySubscription && !allPrimaryHaveItsBundle) {
        this.$refs.primarySubscriptionWarning.show();
      } else {
        this.moveDraggedSubscription();
        this.$confirmAction('');
      }
    },
    getIsDroppable(target) {
      const selectedMessageInboxElement = document.querySelector('.message-inbox[data-selected="true"]');
      const isSameInboxFolder = isEqual(this.subscriptionFolder, this.targetFolder);
      const isValidTarget = selectedMessageInboxElement.contains(target);
      return isValidTarget && !isSameInboxFolder;
    },
    async moveDraggedSubscription() {
      const folderId = this.targetFolder?.type === InboxFolderType.FOLDER ? this.targetFolder.id : null;
      const subscriptionIds = this.draggedSubscriptions.map(subscription => subscription.subscriptionId);
      const shouldRefetch = this.draggedSubscriptions.some(
        subscription => subscription.isPrimary || subscription.isLast
      );
      await this.moveSubscriptionsToFolder(subscriptionIds, folderId);
      if (shouldRefetch) {
        this.fetchThreads();
        return;
      }
      const bundleIds = [
        ...new Set(
          this.draggedSubscriptions
            .filter(subscription => subscription.bundleId != null)
            .map(subscription => subscription.bundleId)
        ),
      ];

      bundleIds.forEach(bundleId => {
        this.loadThreadsInBundleById({ bundleId });
      });
    },
    onBottomReached() {
      if (this.moreSubscriptionsExist && !this.isLoadingThreads) {
        this.loadMoreThreads();
      }
    },
    loadMoreThreads() {
      let loadMoreAction = this.loadMoreSubscriptions;
      if (this.inSearch) {
        loadMoreAction = this.runAdvancedSearch;
        this.setMessageSearchQuery(this.searchModel.getQuery());
        this.searchModel.setOffset(this.subscriptions.length);
      }

      this.setIsLoadingThreads(true);
      return loadMoreAction().then(() => {
        this.setIsLoadingThreads(false);
      });
    },
    setTypeaheadSearchValue(value) {
      this.searchModel.setQuery(value);
    },
    setAdvancedSearchQuery(query) {
      this.advancedSearchQuery = query;
    },
    clearAllInput() {
      this.advancedSearchQuery = '';
      this.advancedSearchSubject = '';
      this.advancedSearchMessageContent = '';
      this.advancedSearchFilter = messageFiltersEnum.FILTER_ALL;
      this.advancedSearchFromDate = null;
      this.advancedSearchToDate = null;
      this.advancedSearchThreadCreators = [];
      this.advancedSearchParticipants = [];
      this.advancedSearchWithAttachments = false;

      this.searchModel.setQuery('');
      this.searchModel.resetSearchParams();

      this.setChosenFilter(this.advancedSearchFilter);

      this.getAllSubscriptionsWhenBlank('');
      this.emitSelect(null);
      this.canShowFolderBadge = false;
    },
    setAdvancedSearchSubject(subject) {
      this.advancedSearchSubject = subject;
    },
    setAdvancedSearchMessageContent(messageContent) {
      this.advancedSearchMessageContent = messageContent;
    },
    setAdvancedSearchFilter(filter) {
      this.advancedSearchFilter = filter;
    },
    setAdvancedSearchFromDate(fromDate) {
      this.advancedSearchFromDate = fromDate;
    },
    setAdvancedSearchToDate(toDate) {
      this.advancedSearchToDate = toDate;
    },
    setAdvancedSearchThreadCreators(threadCreators) {
      this.advancedSearchThreadCreators = threadCreators;
    },
    setAdvancedSearchParticipants(participants) {
      this.advancedSearchParticipants = participants;
    },
    setAdvancedSearchWithAttachments(withAttachments) {
      this.advancedSearchWithAttachments = withAttachments;
    },
    clearSearch() {
      this.$refs.searchField.resetSearchWithoutUpdate();
    },
    setLatestMessage(subscription, message) {
      this.subscriptions[subscription].latestMessage = message;
      this.subscriptions[subscription].lastReadMessageId = message.id;
    },
    openDraftThread() {
      this.openThread({ subscriptionId: this.currentSubscriptionId });
    },
    checkIfOpenThread() {
      if (this.isDraftSelected) {
        this.openDraftThread();
      }

      const subscription = this.subscriptionsAndBundleThreads.find(
        subscription => subscription.id === this.currentSubscriptionId
      );
      const isSensitive = subscription?.sensitive || this.selectedDraft.sensitive;

      if (isSensitive && !this.isSteppedUp) {
        this.setStepUpNotification({
          showStepUpNotification: true,
          redirectedUrl: window.location.href,
          parent: parentTypes.MESSAGES,
        });
        this.$router.push({ name: 'messages' });
        return;
      }

      if (subscription && !this.isDraftSelected) {
        this.openThreadOnDevice(subscription);
      }
    },
    openThreadOnDevice(subscription) {
      if (this.isMobile && this.subscriptionPressed) {
        return;
      }

      this.cancelPreviousOpenThreadRequest();
      this.generateOpenThreadCancelTokenSource();
      this.deleteNotificationsForThread(subscription.id);
      this.setConversationLoadingState(true);
      this.openThread({
        subscriptionId: subscription.id,
        subscriptionLeft: subscription.leaveTime != null,
        mailOwnerId: this.getMailOwnerId(subscription),
        otpInboxId: this.getOtpInboxId(subscription),
        cancelToken: this.openThreadCancelTokenSource.token,
      }).then(
        () => {
          this.resetOpenThreadCancelTokenSource();
          this.highlightAndScrollToWord();
          this.setConversationLoadingState(false);
          this.restartPollingService();
          this.emitSelect(subscription.folderId);
        },
        () => null
      );
    },
    getMailOwnerId(subscription) {
      let mailOwnerId = null;
      if (
        this.inSearch &&
        subscription.mailBoxOwnerType == messageOwnerTypes.COMMON_INBOX &&
        this.commonInboxes.find(c => c.id === subscription.mailBoxOwnerId)
      ) {
        mailOwnerId = subscription.mailBoxOwnerId;
      } else if (!this.inSearch && this.chosenFolderAndMailOwner.mailOwnerType == messageOwnerTypes.COMMON_INBOX) {
        mailOwnerId = this.chosenFolderAndMailOwner.mailOwnerId;
      }
      return mailOwnerId;
    },
    getOtpInboxId(subscription) {
      let mailOwnerId = null;
      if (
        this.profile.role === portalRoles.OTP &&
        subscription.mailBoxOwner &&
        subscription.mailBoxOwner.mailBoxOwnerType === messageOwnerTypes.OTP_INBOX
      ) {
        mailOwnerId = subscription.mailBoxOwner.id;
        this.chosenFolderAndMailOwner.mailOwnerType = messageOwnerTypes.OTP_INBOX;
        this.chosenFolderAndMailOwner.mailOwnerId = mailOwnerId;
      }
      return mailOwnerId;
    },
    showAdvanceSearchModal() {
      this.advancedSearchQuery = this.searchModel.getQuery();
      this.advancedSearchFilter = this.chosenFolderAndMailOwner.filter;
      this.$refs.advanceSearchModal.show();
    },
    hideAdvancedSearchModal() {
      this.$refs.advanceSearchModal.hide();
    },
    getAllSubscriptionsWhenBlank(val) {
      if (val === '' && !this.searchModel.getIsUsingAdvancedSearch()) {
        if (this.chosenFolderAndMailOwner) {
          this.updateThreadViewAction({
            folderId: this.chosenFolderAndMailOwner.folderId,
            filterType: this.chosenFolderAndMailOwner.filter,
            mailOwnerId: this.chosenFolderAndMailOwner.mailOwnerId,
            sort: this.chosenFolderAndMailOwner.sort,
            order: this.chosenFolderAndMailOwner.order,
          });
        } else {
          this.setIsLoadingThreads(true);
          this.$store.dispatch(types.INIT_SUBSCRIPTIONS).then(() => {
            this.setIsLoadingThreads(false);
          });
        }
        if (
          this.profile.role == portalRoles.EMPLOYEE &&
          this.hasPermission(permissionEnum.INBOX_SET_PERSONAL_AUTOREPLY)
        ) {
          this.$store.dispatch('messages/INIT_AUTOREPLY');
        }
      }
    },
    highlightAndScrollToWord() {
      $('.message-textarea .prefixIcon').focus();
      const query = this.searchModel.getQuery();
      if (query != '') {
        // TODO: Refactor this to use the highlightSubscriptionKeyword filter.
        if ($('.message-body:contains(' + query + '):eq(0)').length > 0) {
          $('.threads-container').animate({
            scrollTop:
              $('.messages-overview').offset().top + $('.message-body:contains(' + query + '):eq(0)').position().top,
          });
        }
        $('.message-body').unhighlight();
        $('.thread-metadata-creator').unhighlight();
        $('.subject-sent-to .subscriber').unhighlight();
        const queryWords = query.split(' ');
        queryWords.sort((a, b) => b.length - a.length);
        for (const word of queryWords) {
          $('.message-body').highlight(word, {
            wordsOnly: true,
            wordsBoundaryEnd: '',
          });
          $('.thread-metadata-creator').highlight(word, {
            wordsOnly: true,
            wordsBoundaryEnd: '',
          });
          $('.subject-sent-to .subscriber').highlight(word, {
            wordsOnly: true,
            wordsBoundaryEnd: '',
          });
        }
      }
    },
    async loadSearch(query) {
      this.searchModel.setQuery(query);
      this.searchModel.setOffset(0);
      this.setIsLoadingThreads(true);
      this.setMessageSearchQuery(query);

      if (query.length > 0) {
        await this.runAdvancedSearch();
      } else {
        this.getAllSubscriptionsWhenBlank();
      }
      this.setIsLoadingThreads(false);
    },
    runTypeaheadSearch(query) {
      this.searchModel.setQuery(query);
      this.loadSearchThread();
      this.resetMultipleSelections();
      this.setCanShowFolderBadge();
    },
    runSearch(query) {
      this.searchModel.setFilter(this.advancedSearchFilter);
      this.searchModel.setQuery(query);
      this.searchModel.setContent(this.advancedSearchMessageContent);
      this.searchModel.setFromDate(this.advancedSearchFromDate);
      this.searchModel.setToDate(this.advancedSearchToDate);
      this.searchModel.setHasAttachments(this.advancedSearchWithAttachments);
      this.searchModel.setParticipants(this.advancedSearchParticipants);
      this.searchModel.setSubject(this.advancedSearchSubject);
      this.searchModel.setThreadCreators(this.advancedSearchThreadCreators);

      this.setChosenFilter(this.advancedSearchFilter);
      this.loadSearchThread();

      this.resetMultipleSelections();
      this.$refs.advanceSearchModal.hide();
      this.setCanShowFolderBadge();
    },
    resetMultipleSelections() {
      this.setSelectedThreadIds([]);
      this.setSelectedThreadFolderIds([]);
      this.setSelectedSubscriptionsIds([]);
      this.setSelectedBundleIds([]);
      deselectAllManagers();
    },
    loadSearchThread() {
      const query = this.searchModel.getQuery();
      if (this.searchModel.getIsUsingAdvancedSearch()) {
        this.loadAdvancedSearch();
      } else {
        if (query) {
          this.loadSearch(query);
        } else {
          this.getAllSubscriptionsWhenBlank(query);
        }
      }
    },
    async loadAdvancedSearch() {
      this.searchModel.setOffset(0);
      this.setIsLoadingThreads(true);
      this.runAdvancedSearch().then(() => {
        this.setIsLoadingThreads(false);
      });
      this.$refs.advanceSearchModal.hide();
    },
    deleteNotificationsForThread(threadId) {
      const notifications = this.notifications
        .filter(n => n.threadId == threadId)
        .map(n => ({
          notificationId: n.notificationId,
          institutionProfileId: n.institutionProfileId,
        }));
      if (notifications.length > 0) {
        this.deleteNotifications({ notifications: notifications });
      }
    },
    cancelPreviousOpenThreadRequest() {
      this.cancelAxiosRequest(this.openThreadCancelTokenSource);
    },
    generateOpenThreadCancelTokenSource() {
      this.openThreadCancelTokenSource = this.getAxiosCancelTokenSource();
    },
    resetOpenThreadCancelTokenSource() {
      this.openThreadCancelTokenSource = null;
    },
    scrollIntoViewOnFocused(e) {
      if (e.code === 'Tab') {
        document.activeElement.scrollIntoView({ block: 'center', inline: 'start' });
      }
    },
    runAdvancedSearch() {
      let fromDate = this.searchModel.getFromDate();
      let toDate = this.searchModel.getToDate();

      if (fromDate) {
        fromDate = dateUtil.format(dateUtil.startOf(fromDate, 'day'), 'YYYY-MM-DDTHH:mm:ss');
      }

      if (toDate) {
        toDate = dateUtil.format(dateUtil.endOf(toDate, 'day'), 'YYYY-MM-DDTHH:mm:ss');
      }

      return this.searchSubscriptions({
        text: this.searchModel.getQuery(),
        threadSubject: this.searchModel.getSubject(),
        messageContent: this.searchModel.getContent(),
        fromDate,
        toDate,
        threadCreators: this.searchModel.getThreadCreators(),
        participants: this.searchModel.getParticipants(),
        hasAttachments: this.searchModel.getHasAttachments(),
        offset: this.searchModel.getOffset(),
        activeChildren: this.activeChildren,
        activeInstitutions: this.activeInstitutions,
        profile: this.profile,
      });
    },
    setCanShowFolderBadge() {
      const chosenFolder = this.chosenFolderAndMailOwner;
      const isInbox = chosenFolder.folderName === null;
      const isCommonInbox = chosenFolder.folderName === chosenFolder.commonInboxName;

      this.canShowFolderBadge = isInbox || isCommonInbox;
    },
  },
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/core/scrollbar' as *;
@use '../../../shared/assets/scss/core/animations' as *;

.threads {
  overflow-x: hidden;
  flex-grow: 1;
  flex-basis: 0;

  @include breakpoint-lg {
    position: relative;
    overflow-y: scroll;
    //-webkit-overflow-scrolling: touch;
    overflow-x: hidden;
  }
  @include breakpoint-lg-down() {
    padding-bottom: 80px;
  }

  .searchField {
    position: relative;
    width: 100%;
    margin-bottom: 4px;
    height: 50px;
    font-size: 14px;
    border-radius: 0;
  }

  .firefox & {
    padding-right: 0 !important;
  }

  .loading-spinner {
    margin: 24px auto;
    display: flex;
  }
}

.point-left {
  transform: rotate(-90deg);
  display: block;
  width: 30px;
  font-size: 25px;
}

.is-from-search {
  max-height: none;
}

.backToInbox {
  padding: 14px 20px 14px 0;
  background-color: $color-grey-darker;

  .arrow {
    min-width: 64px;
  }

  .text {
    font-size: 12px;
  }

  i {
    margin-left: 10px;
    cursor: pointer;
    margin-top: -15px;
  }
}

.scrollbar {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  .firefox & {
    padding-right: 17px;
  }

  &:hover {
    padding-right: 0;
  }
}

.searchArea {
  ::v-deep .input-group.search-input-group {
    .input-group-append {
      right: 25px;
      top: 15px;
    }
  }

  @include breakpoint-lg {
    height: 54px;
    width: 100%;
    border-bottom: solid 2px $color-grey-light;
  }
}

.sortable-container[data-is-multiple='true'] ::v-deep .dragging-subscription {
  .subscription {
    box-shadow: var(--box-shadow-base);
  }
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    width: 100%;
    height: 100%;
    background: var(--color-white);
    border: 1px solid #ccc;
    z-index: -1;
    box-shadow: var(--box-shadow-base);
  }
}

::v-deep .dragging-subscription {
  opacity: 0.3 !important;
  box-shadow: var(--box-shadow-base);

  .subscription-touch-actions {
    display: none;
  }
}

::v-deep .ghost-subscription {
  pointer-events: none;

  .subscription {
    background-color: var(--color-white);
    border-radius: 4px;
    outline: 1px dashed var(--color-primary-darker);
    outline-offset: -1px;

    * {
      opacity: 0.6;
    }
  }
}
</style>
